import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Modal } from './Modal';

export interface KanbanColumn {
  id: string;
  title: string;
  color: string;
  order: number;
  isDefault?: boolean;
}

interface ColumnManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  columns: KanbanColumn[];
  onSave: (columns: KanbanColumn[]) => void;
}

export const ColumnManagementModal: React.FC<ColumnManagementModalProps> = ({
  isOpen,
  onClose,
  columns,
  onSave
}) => {
  const [editedColumns, setEditedColumns] = useState<KanbanColumn[]>([]);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setEditedColumns([...columns]);
    }
  }, [isOpen, columns]);

  const addColumn = () => {
    const newColumn: KanbanColumn = {
      id: `col_${Date.now()}`,
      title: 'New Column',
      color: 'var(--accent-blue)',
      order: editedColumns.length
    };
    setEditedColumns([...editedColumns, newColumn]);
  };

  const removeColumn = (index: number) => {
    const column = editedColumns[index];
    if (column.isDefault) return; // Don't allow removing default columns
    
    setEditedColumns(editedColumns.filter((_, i) => i !== index));
  };

  const updateColumn = (index: number, field: keyof KanbanColumn, value: string | number) => {
    const updated = [...editedColumns];
    updated[index] = { ...updated[index], [field]: value };
    setEditedColumns(updated);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const items = [...editedColumns];
    const draggedColumn = items[draggedItem];
    
    // Remove dragged item
    items.splice(draggedItem, 1);
    // Insert at new position
    items.splice(dropIndex, 0, draggedColumn);
    
    // Update order values
    items.forEach((item, index) => {
      item.order = index;
    });

    setEditedColumns(items);
    setDraggedItem(null);
  };

  const handleSave = () => {
    // Update order values before saving
    const finalColumns = editedColumns.map((col, index) => ({
      ...col,
      order: index
    }));
    onSave(finalColumns);
    onClose();
  };

  const predefinedColors = [
    { name: 'Blue', value: 'var(--accent-blue)' },
    { name: 'Green', value: 'var(--accent-green)' },
    { name: 'Red', value: 'var(--accent-red)' },
    { name: 'Orange', value: 'var(--accent-orange)' },
    { name: 'Purple', value: 'var(--accent-purple)' },
    { name: 'Gray', value: 'var(--text-tertiary)' }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Kanban Columns" size="medium">
      <div className="column-management">
        <div className="columns-list">
          {editedColumns.map((column, index) => (
            <div
              key={column.id}
              className="column-item"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="column-drag-handle">
                <GripVertical size={16} />
              </div>
              
              <div className="column-fields">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={column.title}
                    onChange={(e) => updateColumn(index, 'title', e.target.value)}
                    className="form-input"
                    disabled={column.isDefault}
                  />
                </div>
                
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-picker">
                    {predefinedColors.map((color) => (
                      <button
                        key={color.value}
                        className={`color-option ${column.color === color.value ? 'active' : ''}`}
                        style={{ backgroundColor: color.value }}
                        onClick={() => updateColumn(index, 'color', color.value)}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="column-actions">
                {!column.isDefault && (
                  <button
                    className="remove-column-btn"
                    onClick={() => removeColumn(index)}
                    title="Remove column"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                {column.isDefault && (
                  <div className="default-label">Default</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <button className="add-column-btn" onClick={addColumn}>
          <Plus size={16} />
          <span>Add Column</span>
        </button>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>

      <style>{`
        .column-management {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .columns-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 400px;
          overflow-y: auto;
        }

        .column-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          cursor: move;
          transition: all 0.2s ease;
        }

        .column-item:hover {
          border-color: var(--border-primary);
          box-shadow: var(--shadow-light);
        }

        .column-drag-handle {
          color: var(--text-tertiary);
          cursor: grab;
          margin-top: 8px;
        }

        .column-drag-handle:active {
          cursor: grabbing;
        }

        .column-fields {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .form-group label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .color-picker {
          display: flex;
          gap: 6px;
        }

        .color-option {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid var(--border-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .color-option:hover {
          transform: scale(1.1);
        }

        .color-option.active {
          border-color: var(--text-primary);
          box-shadow: 0 0 0 2px var(--bg-primary), 0 0 0 4px var(--text-primary);
        }

        .column-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          margin-top: 8px;
        }

        .remove-column-btn {
          background: var(--accent-red);
          color: white;
          border: none;
          padding: 6px;
          border-radius: var(--radius-small);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .remove-column-btn:hover {
          opacity: 0.8;
        }

        .default-label {
          font-size: 10px;
          color: var(--text-tertiary);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .add-column-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          background: var(--bg-secondary);
          border: 1px dashed var(--border-primary);
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-column-btn:hover {
          background: var(--border-secondary);
          border-style: solid;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid var(--border-secondary);
        }
      `}</style>
    </Modal>
  );
};